import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import {Select } from '@mantine/core';
import { Button, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';
import CommonTable from '../common/CommonTable';
// Function to calculate days remaining from expiry date to today (June 03, 2025)
const calculateDaysRemaining = (expiryDate) => {
  const today = new Date('2025-06-03'); // Current date
  const expiry = new Date(expiryDate);
  const diffTime = expiry - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays >= 0 ? diffDays : 0;
};

// Columns definition
const columns = [
  {
    accessorKey: 'license_id',
    header: 'License ID',
    size: 150,
    minSize: 100,
     Cell: ({ cell }) => {
      return <span>L0-{cell.getValue()}</span>
    },
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 600,
        color: '#595C60',
      },
    },
  },
 {
    accessorKey: 'license_type',
    header: 'Type',
    size: 160,
    minSize: 100,
     Cell: ({ cell }) => {
      const data=cell.getValue()
      return <span>{data}</span>
    },
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 600,
        color: '#595C60',
      },
    },
  },
  {
    accessorKey: 'takenOn',
    header: 'Taken On',
    size: 150,
    minSize: 120,
    Cell: ({ cell }) => {
        const date = cell.getValue();
        return date
          ? new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
          : 'N/A';
      },
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 600,
        color: '#595C60',
      },
    },
  },
  {
    accessorKey: 'expiry_date',
    header: 'Expiry Date',
    size: 150,
    minSize: 120,
    Cell: ({ cell }) => {
        const date = cell.getValue();
        return date
          ? new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
          : 'N/A';
      },
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 600,
        color: '#595C60',
      },
    },
  },
  {
    accessorKey: 'days_remaining',
    header: 'Days Remaining',
    size: 150,
    minSize: 100,
    Cell: ({ cell }) => {
      const textColor = cell.getValue() <= 30 ? '#E74C3C' : cell.getValue() <= 90 ? '#FF9F43' : '#2ECC71';
      return (
        <span style={{ color: textColor }}>
          {cell.getValue()} days
        </span>
      );
    },
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 600,
      },
    },
  },
];

// Function to export data as CSV
const exportToCSV = (data, filename) => {
  if (!Array.isArray(data)) {
    console.warn('exportToCSV: Data is not an array, defaulting to empty array.');
    return;
  }

  const headers = [
    'License ID',
    'Licenses Taken',
    'Licenses Applied',
    'Taken On Date',
    'Expiry Date',
    'Days Remaining',
  ];
  const rows = data.map(item => [
    item.licenseId,
    item.licensesTaken,
    item.licensesApplied,
    item.takenOnDate,
    item.expiryDate,
    calculateDaysRemaining(item.expiryDate),
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(',')),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.click();
  URL.revokeObjectURL(url);
};

const UtilisedTable = ({ data }) => {
  const table = useMantineReactTable({
    columns,
    data: data || [],
    enableRowSelection: true,
    enableStickyHeader: true,
    enableColumnResizing: true,
    enableColumnHiding: true,
    enablePagination: true,
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    mantineTableContainerProps: {
      style: {
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: '400px',
        paddingLeft: '20px',
        paddingRight: '20px',
      },
    },
    mantineTableProps: {
      style: {
        minWidth: 'max-content',
        tableLayout: 'fixed',
        fontFamily: 'Nunito Sans, sans-serif',
        backgroundColor: '#ffffff',
        width: '100%',
      },
      className: 'shadow-sm rounded-lg',
    },
    mantineTableHeadCellProps: {
      style: {
        whiteSpace: 'nowrap',
        backgroundColor: '#F1F4F9',
        borderBottom: '1px solid #e5e7eb',
        padding: '12px',
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: '700',
        fontSize: '14px',
        color: '#202224',
      },
    },
    mantineTableBodyRowProps: {
      style: {
        borderBottom: '0.5px solid #979797',
        backgroundColor: '#ffffff',
        '&:hover': {
          backgroundColor: '#f5f5f5 !important',
        },
      },
    },
    mantineTableBodyCellProps: {
      style: {
        whiteSpace: 'nowrap',
        padding: '12px',
        height: '48px',
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: '600',
        color: '#595C60',
        backgroundColor: 'transparent !important',
        '&:hover': {
          backgroundColor: 'transparent !important',
        },
      },
    },
    mantineSelectCheckboxProps: {
      size: 'sm',
      styles: {
        input: {
          '&:not(:checked)': {
            border: '1px solid #202224 !important',
            backgroundColor: 'transparent !important',
          },
          '&:checked': {
            border: 'none !important',
            backgroundColor: '#FF7A00 !important',
          },
        },
        icon: {
          color: '#ffffff !important',
        },
        wrapper: {
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'transparent !important',
        },
      },
      className: 'custom-checkbox-cell',
    },
    mantineSelectAllCheckboxProps: {
      size: 'sm',
      styles: {
        input: {
          '&:not(:checked)': {
            border: '1px solid #202224 !important',
          },
          '&:checked': {
            border: 'none !important',
            backgroundColor: '#FF7A00 !important',
          },
        },
        icon: {
          color: '#ffffff !important',
        },
        wrapper: {
          borderBottom: '1px solid #e5e7eb',
        },
      },
    },
    mantineColumnVisibilityToggleButtonProps: {
      sx: {
        color: '#FF7A00 !important',
        fontFamily: 'Nunito Sans, sans-serif',
        fontSize: '14px',
        border: 'none',
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: '#FFF3E5',
        },
        '& svg': {
          color: '#FF7A00 !important',
        },
      },
    },
    mantineTableToolbarProps: {
      sx: {
        '& .mantine-Menu-item': {
          color: '#FF7A00 !important',
          fontFamily: 'Nunito Sans, sans-serif',
          fontSize: '14px',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: '#FFF3E5 !important',
          },
        },
        '& .mantine-Menu-item[data-testid="mrt-column-visibility-show-all-button"], & .mantine-Menu-item[data-testid="mrt-column-visibility-hide-all-button"]': {
          color: '#FF7A00 !important',
          '&:hover': {
            backgroundColor: '#FFF3E5 !important',
          },
        },
      },
    },
    mantinePaginationProps: {
      styles: {
        root: {
          fontFamily: 'Nunito Sans, sans-serif',
        },
        control: {
          backgroundColor: '#FF7A00',
          color: '#fff',
          border: 'none',
          '&:hover': {
            backgroundColor: '#FF6B00',
          },
          '&[data-disabled]': {
            backgroundColor: '#A0AEC0',
            color: '#EDF2F7',
          },
        },
        select: {
          fontFamily: 'Nunito Sans, sans-serif',
          border: '0.6px solid #202224',
          borderRadius: '6px',
          backgroundColor: '#FCFDFD',
          '&:focus': {
            borderColor: '#FF6B00',
            outline: 'none',
          },
        },
      },
    },
  });

  return (
    <div className="mb-5">
      
      <div className="flex justify-between items-center mb-4">
        <Typography variant='h4'>
          License Overview
        </Typography>
        <div className="flex items-center">
          <Button
            size="sm"
            variant="filled"
            className="export-button"
           
            onClick={(e) => {
              const button = e.currentTarget;
              const ripple = document.createElement('span');
              const diameter = Math.max(button.clientWidth, button.clientHeight);
              const radius = diameter / 2;

              ripple.style.width = ripple.style.height = `${diameter}px`;
              ripple.style.left = `${e.clientX - button.getBoundingClientRect().left - radius}px`;
              ripple.style.top = `${e.clientY - button.getBoundingClientRect().top - radius}px`;
              ripple.classList.add('export-button-ripple');

              button.appendChild(ripple);

              setTimeout(() => {
                ripple.remove();
              }, 600);

              exportToCSV(data, 'license-utilised.csv');
            }}
          >
            <FontAwesomeIcon
              icon={faDownload}
              style={{
                height: '10px',
                width: '10px',
                color: '#fff',
              }}
            />
            <span>Export</span>
          </Button>        
        </div>
      </div>
      <CommonTable data={data} columns={columns} />
    </div>
  );
};

export default UtilisedTable;